import { useCallback, useEffect, useMemo, useState } from 'react';

// Local, per-browser edits layered over the inventory from the API: field
// overrides, hidden screens, and hand-placed markers. Persisted in
// localStorage so they survive refreshes.

const STORAGE_KEY = 'ss.screenEdits.v1';

export interface ScreenOverride { name?: string; venue?: string; lat?: number; lon?: number; hidden?: boolean }
export interface CustomScreen { id: string; name: string; venue: string; lat: number; lon: number }
interface Stored { overrides: Record<string, ScreenOverride>; custom: CustomScreen[] }

function load(): Stored {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      return { overrides: p.overrides ?? {}, custom: p.custom ?? [] };
    }
  } catch { /* storage unavailable or corrupt */ }
  return { overrides: {}, custom: [] };
}

interface Base { id: string; name: string; venue: string; lat: number; lon: number }

export function useScreenEdits<T extends Base>(base: T[], makeCustom: (c: CustomScreen) => T) {
  const [state, setState] = useState<Stored>(load);

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
  }, [state]);

  const screens = useMemo(() => {
    const merged = base
      .filter(s => !state.overrides[s.id]?.hidden)
      .map(s => {
        const { hidden, ...o } = state.overrides[s.id] ?? {};
        return { ...s, ...o };
      });
    return [...merged, ...state.custom.map(makeCustom)];
  }, [base, state, makeCustom]);

  const update = useCallback((id: string, patch: ScreenOverride) => {
    setState(st => st.custom.some(c => c.id === id)
      ? { ...st, custom: st.custom.map(c => c.id === id ? { ...c, ...patch } : c) }
      : { ...st, overrides: { ...st.overrides, [id]: { ...st.overrides[id], ...patch } } });
  }, []);

  const addCustom = useCallback((lat: number, lon: number) => {
    const id = `custom-${Date.now().toString(36)}`;
    setState(st => ({ ...st, custom: [...st.custom, { id, name: 'New screen', venue: '', lat, lon }] }));
    return id;
  }, []);

  const remove = useCallback((id: string) => {
    setState(st => st.custom.some(c => c.id === id)
      ? { ...st, custom: st.custom.filter(c => c.id !== id) }
      : { ...st, overrides: { ...st.overrides, [id]: { ...st.overrides[id], hidden: true } } });
  }, []);

  const reset = useCallback((id?: string) => {
    setState(st => {
      if (!id) return { overrides: {}, custom: [] };
      const { [id]: _drop, ...rest } = st.overrides;
      return { ...st, overrides: rest };
    });
  }, []);

  const isEdited = useCallback((id: string) => !!state.overrides[id] || state.custom.some(c => c.id === id), [state]);
  const editCount = Object.keys(state.overrides).length + state.custom.length;

  return { screens, update, addCustom, remove, reset, isEdited, editCount };
}
